// src/components/AvatarDropdown/AvatarDropdown.tsx

import React, { useContext } from "react";
import { Popover, Transition } from "@headlessui/react";
import { Link } from "react-router-dom";
import { CartContext } from "../../containers/PageCheckout/CartContext";
import { useAuth } from "../../api/AuthContext";
import { LogOut, User as UserIcon } from "lucide-react";

const dropdownLinkClass = "dropdown-link flex flex-row items-center";

export default function AvatarDropdown() {
  const { user, loading, logout } = useAuth();
  const cartContext = useContext(CartContext);
  const clearCart = cartContext?.clearCart;

  const isLoggedIn = !!user;
  const userName = user?.fullName || user?.email || "Guest";

  const handleLogout = async (close: () => void) => {
    await logout();
    clearCart?.();
    close();
  };

  return (
    <div className="AvatarDropdown flex shrink-0 items-center">
      <Popover className="relative">
        {({ close }) => (
          <>
            <Popover.Button
              className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full p-0 text-slate-700 hover:bg-slate-100 focus:outline-none dark:text-slate-300 dark:hover:bg-slate-800 sm:h-12 sm:w-12"
              aria-label={isLoggedIn ? "Open account menu" : "Open login menu"}
            >
              {loading ? (
                <span className="h-7 w-7 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700 sm:h-8 sm:w-8" />
              ) : isLoggedIn && user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={`${userName} profile`}
                  className="h-7 w-7 rounded-full object-cover sm:h-8 sm:w-8"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <svg
                  className="h-6 w-6 shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20.5899 22C20.5899 18.13 16.7399 15 11.9999 15C7.25991 15 3.40991 18.13 3.40991 22"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </Popover.Button>

            <Transition
              as={React.Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 w-screen max-w-[260px] px-4 mt-3.5 -right-10 sm:right-0 sm:px-0">
                <div className="overflow-hidden rounded-3xl shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="relative grid grid-cols-1 gap-6 bg-white dark:bg-neutral-800 py-7 px-6">
                    <div className="flex items-center space-x-3">
                      <div className="flex-grow">
                        <h4 className="font-semibold text-lg text-slate-700 dark:text-slate-200">
                          {userName}
                        </h4>
                      </div>
                    </div>

                    {isLoggedIn && (
                      <>
                        <div className="w-full border-b border-neutral-200 dark:border-neutral-700" />
                        <Link to="/account" className={dropdownLinkClass} onClick={() => close()}>
                          <UserIcon className="w-5 h-5" aria-hidden="true" />
                          <span className="ml-4 text-sm font-medium">My Account</span>
                        </Link>
                      </>
                    )}

                    <div className="w-full border-b border-neutral-200 dark:border-neutral-700" />
                    {isLoggedIn ? (
                      <Link
                        to="#"
                        className={dropdownLinkClass}
                        onClick={(e) => {
                          e.preventDefault();
                          handleLogout(close);
                        }}
                      >
                        <LogOut className="w-5 h-5" aria-hidden="true" />
                        <span className="ml-4 text-sm font-medium">Logout</span>
                      </Link>
                    ) : (
                      <Link to="/login" className={dropdownLinkClass} onClick={() => close()}>
                        <LogOut className="w-5 h-5" aria-hidden="true" />
                        <span className="ml-4 text-sm font-medium">Login</span>
                      </Link>
                    )}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
}
